import { STOCKTAKE_ROLES } from '../../constants/stocktakeRoles';
import React from 'react';
import { Link } from 'react-router-dom';
import {
    Avatar,
    Card,
    CardActionArea,
    Container,
    Grid,
    Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { UserType } from '../../interfaces/userType.enum';
import { useDashboardAccess } from '../dashboard/useDashboardAccess';
import { MODULE_CATALOG, MODULE_GROUPS, ModuleCard } from './moduleCatalog';

/**
 * "All Modules" launcher — one card per top-level module the signed-in user can reach.
 *
 * Visibility must agree with the sidebar, so the permission checks below are the same
 * `usePermission(...)` calls (same keys, same role fallbacks) that Sidebar.tsx makes. Hooks run
 * unconditionally at the top, exactly as they do there; the per-card decision is a plain lookup.
 */
const ModulesPage = () => {
    const { user } = useAuth();
    const { enabledModules } = useDashboardAccess();

    const userRoles = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];
    const hasRole = (role: UserType) => userRoles.includes(role);

    const isSystemAdmin = hasRole(UserType.SystemAdmin);
    const isInstitutionAdmin = hasRole(UserType.InstitutionAdmin);
    const isInstitutionManager = hasRole(UserType.InstitutionManager);
    const isStoreAdmin = hasRole(UserType.StoreAdmin);
    const isAccountant = hasRole(UserType.Accountant);
    const isStoreManager = hasRole(UserType.StoreManager);

    const canAccessUsers = usePermission(PERMISSIONS.MENU_USERS, isInstitutionAdmin);
    const canAccessStores = usePermission(PERMISSIONS.MENU_STORES, isInstitutionAdmin);
    const canAccessAccounting = usePermission(PERMISSIONS.MENU_ACCOUNTING, isAccountant || isInstitutionAdmin);
    const canAccessItemManagement = usePermission(PERMISSIONS.MENU_ITEMS, isInstitutionAdmin || isStoreAdmin);
    const canAccessStocktakes = usePermission(PERMISSIONS.STOCKTAKE_VIEW, STOCKTAKE_ROLES.some(hasRole));
    const canAccessStockTransfers = usePermission(PERMISSIONS.STOCK_TRANSFER_VIEW, isInstitutionAdmin || isStoreAdmin);

    // Procurement / Sales / POS are role-gated in the sidebar with no permission key of their own.
    const canAccessProcurement = isInstitutionAdmin || isInstitutionManager || isStoreAdmin || isAccountant;
    const canAccessSales = isInstitutionAdmin || isInstitutionManager || isStoreAdmin || isAccountant;
    // Union of every POS screen's role set (task-3 review F4): PosTerminalController admits
    // InstitutionAdmin/StoreAdmin/StoreManager, PosSettingsController's GET admits
    // InstitutionAdmin/StoreAdmin/Accountant. No single `path` serves both StoreManager and a
    // store-role-less Accountant, so the card's `pathFor` (moduleCatalog.ts) resolves that per
    // role at render time -- this gate only decides whether the card shows at all.
    const canAccessPos = isInstitutionAdmin || isStoreAdmin || isStoreManager || isAccountant;

    const visibility: Record<string, boolean> = {
        users: canAccessUsers,
        stores: canAccessStores,
        items: canAccessItemManagement,
        stocktakes: canAccessStocktakes,
        stockTransfers: canAccessStockTransfers,
        procurement: canAccessProcurement,
        accounting: canAccessAccounting,
        sales: canAccessSales,
        pos: canAccessPos,
        billing: isInstitutionAdmin,
        institutionSettings: isInstitutionAdmin && !isSystemAdmin,
        institutions: isSystemAdmin,
    };

    // One flat grid, no section headings: a shop has few enough modules that grouping them added
    // scrolling and reading without adding information. MODULE_GROUPS survives purely as the sort
    // key, so the sequence is unchanged -- POS first, the Settings screens last -- and the sort is
    // stable, keeping each group's own catalog order within it.
    const visibleCards = MODULE_CATALOG
        .filter(m => visibility[m.key] === true && (!m.gatedModule || enabledModules.has(m.gatedModule)))
        .sort((a, b) => MODULE_GROUPS.indexOf(a.group) - MODULE_GROUPS.indexOf(b.group));

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h5" fontWeight={600}>All Modules</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                Everything you have access to, in one place.
            </Typography>

            <Grid container spacing={2}>
                {visibleCards.map(card => <ModuleTile key={card.key} card={card} roles={userRoles} />)}
            </Grid>

            {visibleCards.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No modules are available for your account yet.
                </Typography>
            )}
        </Container>
    );
};

const ModuleTile = ({ card, roles }: { card: ModuleCard; roles: string[] }) => {
    const Icon = card.icon;
    const path = card.pathFor?.(roles) ?? card.path;
    return (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Card
                variant="outlined"
                sx={{
                    height: '100%',
                    borderRadius: 2,
                    transition: 'box-shadow .2s ease, transform .2s ease',
                    '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                }}
            >
                <CardActionArea
                    component={Link}
                    to={path}
                    sx={{
                        height: '100%',
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                    }}
                >
                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', mb: 1.5 }}>
                        <Icon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                        {card.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {card.blurb}
                    </Typography>
                </CardActionArea>
            </Card>
        </Grid>
    );
};

export default ModulesPage;
